import { StockNumericType, MoodNumericType, StockEnum } from "data/enums";
import i18n from "i18n/i18n";

export function parseIntToStockNumericType(value: number): StockNumericType | undefined {
    switch (value) {
        case 0:
            return StockNumericType.EXPERIENCE;
        case 1:
            return StockNumericType.STAY;
        case 2:
            return StockNumericType.HOLIDAY_PACKAGE;
        case 3:
            return StockNumericType.EVENT;
        default:
            return undefined;
    }
}

export function parseStringToStockNumericType(value: string): StockNumericType | undefined {
    switch (value.toLowerCase()) {
        case "experience":
        case "experiences":
        case "esperienza":
        case "esperienze":
            return StockNumericType.EXPERIENCE;
        case "stay":
        case "stays":
        case "accommodation":
        case "accommodations":
        case "property":
        case "properties":
        case "soggiorno":
        case "soggiorni":
            return StockNumericType.STAY;
        case "holiday_package":
        case "holidaypackage":
        case "holiday-package":
        case "holiday-packages":
        case "pacchetto-viaggio":
        case "pacchetti-viaggio":
            return StockNumericType.HOLIDAY_PACKAGE;
        case "events":
        case "event":
        case "evento":
        case "eventi":
            return StockNumericType.EVENT;
        default:
            return undefined;
    }
}

/**
 * SEO-friendly URL slugs per language. Used in routes like
 * `/holiday-packages` (en) or `/pacchetti-viaggio` (it).
 * Every slug here maps back via parseStringToStockNumericType.
 */
const STOCK_TYPE_SLUGS: Record<"en" | "it", Record<StockNumericType, string>> = {
    en: {
        [StockNumericType.EXPERIENCE]: "experiences",
        [StockNumericType.STAY]: "stays",
        [StockNumericType.HOLIDAY_PACKAGE]: "holiday-packages",
        [StockNumericType.EVENT]: "events",
    },
    it: {
        [StockNumericType.EXPERIENCE]: "esperienze",
        [StockNumericType.STAY]: "soggiorni",
        [StockNumericType.HOLIDAY_PACKAGE]: "pacchetti-viaggio",
        [StockNumericType.EVENT]: "eventi",
    },
};

/**
 * Builds the SEO-friendly slug for a stock type, localised to the active
 * language (Italian gets Italian slugs; en/es fall back to English).
 * Pass `lang` explicitly to override the current i18n language.
 */
export function stockTypeToSlug(type: StockNumericType, lang?: string): string {
    const language = (lang ?? i18n.language ?? "en").toLowerCase();
    const table = language.startsWith("it") ? STOCK_TYPE_SLUGS.it : STOCK_TYPE_SLUGS.en;
    return table[type] ?? STOCK_TYPE_SLUGS.en[StockNumericType.STAY];
}

/**
 * Singular, SEO-friendly slugs for detail pages, per language. Used in routes
 * like `/holiday-package/:id` (en) or `/pacchetto-viaggio/:id` (it).
 * Singular intentionally differs from the plural list slugs (STOCK_TYPE_SLUGS).
 */
const STOCK_TYPE_DETAIL_SLUGS: Record<"en" | "it", Record<StockNumericType, string>> = {
    en: {
        [StockNumericType.EXPERIENCE]: "experience",
        [StockNumericType.STAY]: "stay",
        [StockNumericType.HOLIDAY_PACKAGE]: "holiday-package",
        [StockNumericType.EVENT]: "event",
    },
    it: {
        [StockNumericType.EXPERIENCE]: "esperienza",
        [StockNumericType.STAY]: "soggiorno",
        [StockNumericType.HOLIDAY_PACKAGE]: "pacchetto-viaggio",
        [StockNumericType.EVENT]: "evento",
    },
};

/**
 * Builds the localised detail-page slug for a stock type (singular form).
 */
export function stockTypeToDetailSlug(type: StockNumericType, lang?: string): string {
    const language = (lang ?? i18n.language ?? "en").toLowerCase();
    const table = language.startsWith("it") ? STOCK_TYPE_DETAIL_SLUGS.it : STOCK_TYPE_DETAIL_SLUGS.en;
    return table[type] ?? STOCK_TYPE_DETAIL_SLUGS.en[StockNumericType.STAY];
}

/**
 * Builds the full localised detail-page path, e.g. `/pacchetto-viaggio/<uuid>`.
 */
export function stockDetailPath(type: StockNumericType, id: string | number): string {
    return `/${stockTypeToDetailSlug(type)}/${id}`;
}

/**
 * Parses the `:defaultType` route param into a StockNumericType.
 * Accepts the new SEO slugs (e.g. "holiday-packages") and, for backward
 * compatibility with old links, the legacy numeric ids (e.g. "2").
 */
export function parseRouteParamToStockNumericType(value: string | undefined): StockNumericType | undefined {
    if (value === undefined) return undefined;
    const bySlug = parseStringToStockNumericType(value);
    if (bySlug !== undefined) return bySlug;
    const asInt = parseInt(value, 10);
    return Number.isNaN(asInt) ? undefined : parseIntToStockNumericType(asInt);
}

export function parseNumericToStockEnum(value: number): StockEnum | undefined {
    switch (value) {
        case 0:
            return StockEnum.EXPERIENCE;
        case 1:
            return StockEnum.STAY;
        case 2:
            return StockEnum.HOLIDAY_PACKAGE;
        case 3:
            return StockEnum.EVENT;
        default:
            return undefined;
    }
}

export function parseStringToMoodNumericType(value: string): MoodNumericType | undefined {
    switch (value) {
        case "comfort":
            return MoodNumericType.COMFORT;
        case "business":
            return MoodNumericType.BUSINESS;
        case "authentic":
            return MoodNumericType.AUTHENTIC;
        default:
            return undefined;
    }
}
